import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader, ProvenanceBadge } from "@/components/Common";
import AIDraftDialog from "@/components/AIDraftDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AIStudio() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [selected, setSelected] = useState(null); // slug
  const [detail, setDetail] = useState(null);
  const [values, setValues] = useState({});
  const [prov, setProv] = useState({});
  const [aiField, setAiField] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => {
      const open = (data.roadmap || []).filter((t) => t.status !== "locked");
      setTasks(open);
      const focus = data.focus?.slug || open[0]?.slug;
      if (focus) load(focus);
    }).catch(() => setTasks([]));
  }, []);

  const load = (slug) => {
    setSelected(slug);
    setLoadingDetail(true);
    api.get(`/tasks/${slug}`).then(({ data }) => {
      setDetail(data.task);
      const v = {};
      (data.draft?.fields || {}) && data.task.fields.forEach((f) => (v[f.key] = data.draft?.fields?.[f.key] || ""));
      setValues(v);
      setProv(data.draft?.provenance || {});
    }).finally(() => setLoadingDetail(false));
  };

  const applyAI = async (key, text, edited) => {
    const nextVals = { ...values, [key]: text };
    const nextProv = { ...prov, [key]: edited ? "ai_assisted_edited" : "ai_assisted" };
    setValues(nextVals);
    setProv(nextProv);
    try {
      await api.put(`/tasks/${selected}/draft`, { fields: nextVals, provenance: nextProv });
    } catch {
      toast.error("Saved locally but couldn't sync. Open the task to retry.");
    }
  };

  if (!tasks) return <FullPageLoader label="Opening AI Studio…" />;

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="ai-studio">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">AI Studio</h1>
        </div>
        <p className="mt-1 max-w-xl text-slate-500">
          AI drafts a first version using what it knows about your business. You edit, then apply it to a step.
          Every applied draft is marked as AI assisted. You're always the author.
        </p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="ai-task-picker">
          {tasks.map((t) => (
            <button
              key={t.slug}
              onClick={() => load(t.slug)}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-all duration-200 ${
                selected === t.slug ? "bg-brand text-white" : "border border-brand-line bg-white text-slate-600 hover:border-brand-muted"
              }`}
            >
              {t.title}
            </button>
          ))}
        </div>

        {loadingDetail || !detail ? (
          <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : (
          <div className="mt-6 space-y-4">
            {detail.fields.map((f) => (
              <div key={f.key} className="rounded-xl border border-brand-line bg-white p-5 shadow-soft">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{f.label}</p>
                  <div className="flex items-center gap-2">
                    <ProvenanceBadge provenance={prov[f.key]} />
                    <button
                      data-testid={`studio-draft-${f.key}`}
                      onClick={() => setAiField(f)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white transition-all duration-200 hover:bg-brand-hover"
                    >
                      <Sparkles size={13} /> Draft with AI
                    </button>
                  </div>
                </div>
                {f.hint && <p className="mt-1 text-xs text-slate-500">{f.hint}</p>}
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                  {values[f.key] || <span className="text-slate-400">Nothing yet. Generate a first draft, edit it, and it saves to this step.</span>}
                </p>
              </div>
            ))}
            <button onClick={() => navigate(`/task/${selected}`)} className="inline-flex items-center gap-2 text-sm font-medium text-brand-text hover:underline">
              Open the full step to finish it <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>

      <AIDraftDialog
        open={!!aiField}
        onOpenChange={(o) => !o && setAiField(null)}
        taskSlug={selected}
        field={aiField}
        existing={aiField ? values[aiField.key] : ""}
        onApply={(text, edited) => applyAI(aiField.key, text, edited)}
      />
    </AppShell>
  );
}
